// Brand-specific details for the per-brand product cards on category pages.
// Key: `${productId}:${brandId}` (ids from products.js and brands.js).
// Every field comes from the manufacturer's public information (researched Oct 2026) — keep
// `sources` up to date when ranges change. Missing entries/fields fall back to products.js / brands.js.
//
// Fields: name (card title), description, features[], applications[], sources[]

export const brandProducts = {
  // ---------- Ceilings ----------
  '1:owa': {
    name: 'OWAcoustic Mineral Fibre Ceiling Tiles',
    description:
      'Lay-in mineral wool ceiling tiles from OWA, designed to reduce echo and reverberation. They fit OWAconstruct 24 mm or 15 mm exposed grid systems.',
    features: [
      'Ranges include Sinfonia (White, Black, Grey), Cosmos and Sandila',
      '1200 × 600 mm and 600 × 600 mm (15 mm thick on Sinfonia White and Cosmos)',
      'Sound absorption up to NRC 0.90, depending on the range',
      'Humidity resistance up to 95% RH',
      'Fire reaction A2-s1,d0 (EN 13501-1)',
    ],
    applications: ['Offices', 'Call centres', 'Studios', 'Hospitals & labs'],
    sources: ['https://www.owa.co.za/owacoustic-mineral-fibre-ceiling-panels/', 'https://www.owa.co.za/product/sinfonia-white/'],
  },
  '1:supertec': {
    name: 'SuperTec Econotile Ceiling Tiles',
    description:
      'Gypsum ceiling tiles with a washable vinyl face and foil backing, laid into suspended exposed-grid ceilings.',
    features: [
      '9 mm and 12 mm thick',
      '600 × 600 mm and 1200 × 600 mm',
      'Shell White and Fissured finishes',
      'Foil backed; taped edges limit water penetration and gypsum dust',
      'Fire classification Class B/B1/2',
    ],
    applications: ['Suspended ceilings', 'Lay-in tile ceilings', 'Offices', 'Retail'],
    sources: ['https://www.supertecceilings.co.za/ceiling-tiles'],
  },
  '2:gyproc': {
    name: 'Gyproc Gypframe Exposed Ceiling Grid',
    description:
      "Saint-Gobain Gyproc's Gypframe range of steel T-bar exposed ceiling grids, made in Germiston, for lay-in tiles in 1200 × 600 or 600 × 600 layouts.",
    features: [
      'T-profile: 38 mm web, 24 mm face',
      '3600 mm main tees; 1200 mm and 600 mm cross tees',
      'Gypframe D38FR fire-resistive grid (with fire-resistive tiles)',
      'Gypframe Essential T38: Frost White capping, Z100 galvanising',
      'White standard; powder coating on request',
    ],
    applications: ['Commercial suspended ceilings', 'Fire-rated ceiling systems', 'Offices & retail'],
    sources: [
      'https://www.gyproc.co.za/products/metal-products/gypframe-d38fr',
      'https://www.gyproc.co.za/documents/product-datasheet/gypframe-essential-exposed-ceiling-grid-technical-data.pdf',
    ],
  },
  '2:supertec': {
    name: 'SuperTec Econoloc Exposed Grid',
    description:
      "SuperTec's galvanised steel exposed T-grid for lay-in ceiling tiles, forming a 600 × 1200 mm grid (or 600 × 600 mm with 600 mm cross tees).",
    features: [
      'Galvanised ISQ300 steel, pre-painted capping in black or white',
      '24 mm face; 38 mm web (main tees), 35 mm web (cross tees)',
      '3.6 m main tees; cross tees 1200 mm and 600 mm (actual 1198 / 598 mm)',
      'Fully knurled, double-stitched main tees with fire punch-outs',
      'Hanger holes at 150 mm centres on main tees',
    ],
    applications: ['Suspended lay-in ceilings', 'Vinyl, acoustic & mineral tiles', 'Offices & retail'],
    sources: ['https://www.supertecceilings.co.za/ceilings'],
  },
  '3:supertec': {
    name: 'SuperTec Econoloc Concealed Plaster Grid',
    description:
      'Concealed steel grid for flush plastered ceilings: plasterboard is screwed to the underside, then taped and skimmed for a seamless finish.',
    features: [
      'Galvanised ISQ300 steel, fully knurled, double-stitched main tees',
      '35 mm face; 38 mm web (main tees), 35 mm web (cross tees)',
      '3.6 m main tees; screw-up cross tees 35 × 38 × 1200 mm (actual 1198 mm)',
      'Main tees at 1.2 m centres, cross tees at 400 mm centres',
      'Takes 9 mm plasterboard screwed at max 150 mm centres',
    ],
    applications: ['Flush plastered ceilings', 'Bulkheads', 'Residential & commercial'],
    sources: ['https://www.supertecceilings.co.za/ceilings'],
  },

  // ---------- Drywall & partitioning ----------
  '4:gyproc': {
    name: 'Gyproc RhinoBoard Plasterboard',
    description:
      "Saint-Gobain Gyproc's RhinoBoard gypsum plasterboard range: standard boards for ceilings and partitions, plus performance boards for moisture, fire and sound.",
    features: [
      'RhinoBoard 6.4 mm for ceilings (fixed to timber or steel brandering)',
      'RhinoBoard 12.5 mm: 1200 mm wide, 2400–3600 mm lengths, tapered edge',
      'RhinoBoard MoistureResistant 12.5 & 15 mm for wet areas and tiling',
      'RhinoBoard FireStop 12.5 & 15 mm: glass-fibre reinforced core',
      'RhinoBoard SoundBloc 12.5 & 15 mm: denser core for sound insulation',
    ],
    applications: ['Ceilings', 'Drywall partitions', 'Wet areas & tiling', 'Fire- & sound-rated walls'],
    sources: ['https://www.gyproc.co.za/product-boards', 'https://www.gyproc.co.za/rhinoboard-original-plasterboards'],
  },
  '4:siniat': {
    name: 'Siniat EasyBoard, MoistureCheck & FireCheck',
    description:
      "Siniat's locally made gypsum board range from Etex: standard EasyBoard, economy BaseBoard, and performance boards for moisture (MoistureCheck) and fire (FireCheck).",
    features: [
      'EasyBoard 6.4 mm square edge for ceilings; 9 & 12 mm tapered edge',
      'MoistureCheck 12 & 15 mm: moisture-resistant core and liners',
      'FireCheck 12 & 15 mm: fire-retardant core (rated in Siniat wall systems)',
      'SABS Mark of Approval on EasyBoard, BaseBoard and MoistureCheck',
      'EasyBoard also GreenTag certified',
    ],
    applications: ['Ceilings & bulkheads', 'Drywalls & wall linings', 'Kitchens & bathrooms', 'Fire-rated walls'],
    sources: ['https://www.siniat.co.za/en-za/products-systems/products/gypsum-boards/'],
  },
  '4:supertec': {
    name: 'SuperTec Gypsum Board',
    description:
      'Gypsum plasterboard stocked under the SuperTec Ceilings & Drywall brand: standard 9 mm and 12 mm boards, plus fire-resistant and moisture-resistant boards.',
    features: [
      'Standard 9 & 12 mm: 1200 mm wide, 2400–3600 mm lengths, tapered edge',
      '15 mm Fire Resistant: fibreglass-reinforced core',
      'Moisture Resistant 12 mm: hydrophobic core, EN 520 Type H',
      'Moisture Resistant board suits tiling in wet-area partitions',
    ],
    applications: ['Ceilings', 'Drywall partitions', 'Bathrooms & kitchens', 'Fire-rated walls'],
    sources: ['https://www.supertecceilings.co.za/downloads'],
  },
  '5:supertec': {
    name: 'SuperTec Steel Brandering',
    description:
      'ISQ 300 steel branders: a timber-free framing system for flush plastered ceilings and bulkheads, hung from the roof structure on suspension brackets.',
    features: [
      'ISQ 300 steel, ribbed for screw location',
      'Swaged ends — no joiner section needed',
      'Lengths 2.7–4.5 m; suspension brackets 150 mm and 300 mm',
      'Branders at max 400 mm centres, brackets max 1.2 m apart',
      "Won't warp, rot or suffer wood-borer damage",
    ],
    applications: ['Flush plastered ceilings', 'Bulkheads', 'Residential ceilings (6.4 / 9 mm board)'],
    sources: ['https://www.supertecceilings.co.za/downloads'],
  },
  '6:supertec': {
    name: 'SuperTec Drywall Studs, Tracks & Aluminium Sections',
    description:
      'Galvanised steel studs and tracks for drywall partitions, plus clip-together aluminium sections for door frames, glazing, wall ends and skirting.',
    features: [
      'Studs 51, 63.5 and 102 mm wide; 2.1–4.2 m lengths',
      'Tracks 52, 65 and 103 mm wide; 3 m lengths',
      'Galvanised ISQ300 steel; knurled for screw location',
      'Aluminium sections (natural anodised) suit 76 mm or 89 mm walls',
      'Aluminium skirting: 75 mm flat, 100 mm flat or 75 mm ribbed',
    ],
    applications: ['Internal partitions', 'Glazed office partitions', 'Door openings', 'High walls'],
    sources: ['https://www.supertecceilings.co.za/partitions'],
  },

  // ---------- Plasters ----------
  '7:siniat': {
    name: 'Siniat EasyLite Skimming Plaster & Jointing Compound',
    description:
      'EasyLite is a lightweight gypsum skimming plaster for internal walls and ceilings (blue bag inland, yellow bag coastal). Premium Jointing Compound is used with Siniat FibaTape on plasterboard joints.',
    features: [
      'EasyLite: 3–75 mm application; 20 kg & 40 kg (blue), 40 kg (coastal yellow)',
      'EasyLite: no sanding required; non-combustible',
      'Jointing Compound: 20 kg bag covers about 40 m of joint',
      'Jointing Compound: about 60 min working time, 130 min final set',
      'Flexible additives reduce joint cracking',
    ],
    applications: ['Skimming walls & ceilings', 'Plasterboard jointing', 'Concrete soffits & brick'],
    sources: [
      'https://www.siniat.co.za/en-za/products-systems/products/finishings/finishers/siniat-easy-lite-gypsum-skimming-plaster/',
      'https://www.siniat.co.za/en-za/products-systems/products/finishings/finishers/siniat-jointing-plaster/',
    ],
  },
  '7:casofour': {
    name: 'Casofour Handskim & Jointfill Plaster',
    description:
      'South African gypsum plasters: Handskim multipurpose skimming plaster (blue for inland, yellow for coastal) and Jointfill (pink) for plasterboard joints.',
    features: [
      'Handskim: 40 kg bag covers about 25 m² at 3 mm',
      'Handskim Blue: about 30 min working time, 65 min final set',
      'Jointfill: 20 kg bag, sandable, about 100–120 min set',
      'Non-combustible',
      'Apply at 5–40 °C',
    ],
    applications: ['Skimming over cement plaster', 'Drywall & ceilings', 'Plasterboard joints', 'Crack & patch filling'],
    sources: ['https://casofour.co.za/'],
  },
  '7:ite': {
    name: 'iTe PATCHiTe & DURALiTe Skim Coat',
    description:
      'iTe makes cement-based repair and skim products: DURALiTe D50 is a cement skim coat for interior and exterior walls, and PATCHiTe is a rapid-set patching compound for floor screeds.',
    features: [
      'DURALiTe D50: 3–10 mm; 35 kg bag covers about 25 m² at 3 mm',
      'DURALiTe D50: does not soften when wet; sandable; interior & exterior',
      'PATCHiTe: 1.5–60 mm in one application',
      'PATCHiTe: rapid set, dries in about 3 hours',
    ],
    applications: ['Wall skim coats', 'Over plaster, concrete, fibre cement & gypsum board', 'Floor screed repairs'],
    sources: ['https://iteproducts.co.za/product/ite-products-duralite-d50-15kg/', 'https://iteproducts.co.za/product/patchite/'],
  },

  // ---------- Insulation ----------
  '8:knauf-insulation': {
    name: 'Knauf Insulation Ceiling Roll',
    description:
      "Glass mineral wool ceiling roll made with Knauf's ECOSE bio-based binder (no added phenol or formaldehyde), for insulating pitched roofs at ceiling level.",
    features: [
      'ECOSE binder — no added phenol or formaldehyde',
      'Euroclass A1 non-combustible',
      '135 mm: R 3.35 m²K/W',
      'Multi Pack: lay as one roll or split into two thinner layers',
      'Acoustic Batt also available for partitions and floors',
    ],
    applications: ['Ceiling insulation', 'New homes', 'Retrofit & top-up', 'Partition walls (Acoustic Batt)'],
    sources: ['https://www.environdec.com/library/epd18734', 'https://knauf.com/en-ZA/p/product/multi-pack-ceiling-roll-26552_4206'],
  },
  '8:isover': {
    name: 'Isover Glasswool Insulation',
    description:
      'Locally made glasswool insulation for ceilings, drywall partitions and metal-roofed buildings — including Cavitybatt and Cavitylite for drywall and Factorylite for industrial roofs.',
    features: [
      'Non-combustible: SANS 428 class A/A1/1; EN 13501 A1-s1,d0',
      'Cavitybatt/Cavitylite drywall batts: 51 / 63 / 102 mm (R 1.34 / 1.66 / 2.68)',
      'Ceiling rolls: 50 / 100 / 135 mm (R 1.14 / 2.50 / 3.38)',
      'Does not support vermin, mould or bacteria',
      'Made to ISO 9001 and ISO 14001',
    ],
    applications: ['Drywall partitions', 'Ceilings', 'Industrial & commercial roofs', 'HVAC ducts'],
    sources: ['https://www.specifile.co.za/wp-content/uploads/2023/05/Isover-Technical-Data-Sheet-Sign-Off-Doc.pdf'],
  },
  '8:aerolite': {
    name: 'Aerolite Soft Touch Ceiling Insulation',
    description:
      'Glasswool ceiling insulation (formerly Aerolite Think Pink) for thermal and acoustic use, supplied in compression-packed rolls laid between the trusses on top of the ceiling.',
    features: [
      '50 / 100 / 135 mm: R 1.14 / 2.50 / 3.38 m²K/W',
      'Rolls 1200 mm wide',
      'Non-combustible: SANS 428 A/A1/1; EN 13501 A1-s1,d0',
      'SABS mark (SABS 1381 Part 1)',
      'Recovers to full thickness in 24–48 hours',
    ],
    applications: ['Residential ceilings', 'Retrofit in existing homes', 'Top-up insulation'],
    sources: ['https://www.isover.co.za/documents/technical-data-sheet/aerolite-soft-touch-tds.pdf'],
  },
  '8:isotherm': {
    name: 'Isotherm Polyester Insulation',
    description:
      'Thermally bonded polyester insulation made from recycled PET bottles, with no added chemicals or fire retardants, for roofs, ceilings and walls.',
    features: [
      '100% thermally bonded polyester from recycled PET bottles',
      'Fire: SANS 428 class B/B1/2; does not smoulder',
      'Moisture absorption no more than 2% by mass',
      'Agrément South Africa certified',
      'Rolls 750 mm and 1200 mm wide',
    ],
    applications: ['Ceilings (flat, domed, cathedral)', 'Drywall & timber-frame walls', 'Geysers & hot-water pipes'],
    sources: ['https://www.isotherm.co.za/isotherm/product-specifications/'],
  },
  '8:youngmans': {
    name: 'Youngman Youngtherm Insulation',
    description:
      'Youngtherm polyester and glasswool ceiling blankets, plus Lambdaboard insulated ceiling boards, Acoustisorb acoustic insulation and reflective radiant barriers.',
    features: [
      'Youngtherm Glass Wool: 50–135 mm (R 1.30–3.52); A1-s1,d0',
      'Youngtherm Polyester: recycled PET, 55–135 mm (R 0.9–2.7)',
      'Lambdaboard PIR ceiling boards: 25–100 mm (R 1.04–4.17)',
      'Acoustisorb recycled-polyester acoustic insulation',
      'Premium radiant barrier: double-sided foil, woven fibreglass core',
    ],
    applications: ['Ceiling insulation', 'Metal building roofs', 'Roof noise & drumming', 'Cavity walls'],
    sources: ['https://youngman.co.za/insulated-ceiling-blankets/', 'https://youngman.co.za/youngtherm-glass-wool/'],
  },

  // ---------- Acoustic ----------
  '9:owa': {
    name: 'OWA Acoustic Ceilings & Absorbers',
    description:
      'OWA acoustic solutions: OWAcoustic mineral fibre ceiling tiles, plus free-hanging canopies, baffles and fabric-covered wall absorbers from the OWAconsult range.',
    features: [
      'Mineral fibre acoustic ceiling tiles in several finishes',
      'FreeStyle wall absorber: Class A sound absorption (EN ISO 11654)',
      'Free-hanging canopies and baffles for exposed soffits',
      'Ranges for fire protection, humidity and hygiene areas',
    ],
    applications: ['Offices & open-plan', 'Education', 'Retail & hospitality', 'Healthcare'],
    sources: ['https://www.owa.co.za/', 'https://www.owa.de/en/product-overview'],
  },
  '9:woodup': {
    name: 'WoodUpp Akupanel Acoustic Wall Panels',
    description:
      'Wood-finished MDF slats mounted on acoustic felt, for walls and ceilings — sold in South Africa by WoodUpp Africa.',
    features: [
      'Real wood veneer or wood-look foil finishes',
      'Acoustic felt from 50% recycled PET (black, warm grey or light beige)',
      '240 × 60 cm and 300 × 60 cm panels',
      'Class A sound absorption when batten-mounted with mineral wool',
      'Fix with screws and/or mounting adhesive',
    ],
    applications: ['Feature walls', 'Offices & boardrooms', 'Living areas', 'Echo reduction'],
    sources: ['https://woodupp.co.za', 'https://woodupp.co.uk/products/acoustic-panel-2400-x-600-x-22-mm-classic-oak-grey-felt'],
  },

  // ---------- Door frames & glazing ----------
  '13:supertec': {
    name: 'SuperTec Aluminium Door Frame Sections',
    description:
      "Clip-together aluminium door frame sections from SuperTec's drywall partition system, used with its head and wall channels and skirting.",
    features: [
      'Natural anodised; powder coating on order',
      'Suit 76 mm and 89 mm drywall partitions',
      'Part of a matching system with termination channels and skirting',
      'Not for fire ratings above 20 minutes',
    ],
    applications: ['Drywall partitions', 'Office door openings', 'Commercial interiors'],
    sources: ['https://www.supertecceilings.co.za/partitions'],
  },
  '13:swartland': {
    name: 'Swartland Kayo Door Frames',
    description:
      "Door frames from Swartland's Kayo range — the budget-friendly line that sits alongside its pine, hardwood-faced and pivot doors.",
    features: [
      "Part of Swartland's Kayo door range",
      'Pairs with Swartland entrance and interior doors',
      'Swartland is a long-established South African door and window maker',
    ],
    applications: ['Residential homes', 'Interior & entrance doors', 'Renovations'],
    sources: ['https://www.swartland.co.za/ranges/kayo'],
  },
  '14:supertec': {
    name: 'SuperTec Aluminium Glazing Sections',
    description:
      'Aluminium glazing sections and glazing beads for glazed drywall partitions, part of the same SuperTec aluminium partition system as its door frames.',
    features: [
      'Glazing sections with matching glazing bead',
      'Natural anodised; powder coating on order',
      'Suit 76 mm and 89 mm drywall partitions',
      'Combine with SuperTec door frame and termination channels',
    ],
    applications: ['Glazed office partitions', 'Reception areas', 'Commercial interiors'],
    sources: ['https://www.supertecceilings.co.za/partitions'],
  },
  '14:swartland': {
    name: 'Swartland Kenzo Aluminium Windows & Doors',
    description:
      "Swartland's Kenzo range of aluminium windows and doors, with strong, lightweight aluminium-alloy frames.",
    features: [
      'Strong, lightweight aluminium-alloy frames',
      'Never need painting; limited maintenance',
      'Wide range of door types, styles and sizes',
      'SANS 613 certified range (selected door products)',
    ],
    applications: ['Residential windows', 'Entrance & sliding doors', 'New builds & renovations'],
    sources: ['https://www.swartland.co.za/kenzo-aluminium-windows-and-doors'],
  },

  // ---------- Ladders ----------
  '12:mecoladder': {
    name: 'MecoLadder Aluminium Ladders',
    description:
      'South African-made aluminium ladders (since 1981): A-frame step, extension, single, multi-purpose and shelf ladders, plus fibreglass ladders and scaffolding towers.',
    features: [
      'High-strength aluminium alloy; serrated slip-resistant treads',
      'Heavy-duty A-frames rated 150 kg (up to 3.0 m); light-duty 100 kg',
      'Push-up extension ladders with 4 or 6 rung overlap',
      '3-in-1 multi-purpose ladder rated 150 kg',
      'Fibreglass ladders for electrical work',
    ],
    applications: ['Construction sites', 'Industrial', 'Agricultural', 'Household'],
    sources: ['https://mecoladder.co.za/our-ladders/'],
  },
};

export const getBrandProduct = (productId, brandId) => brandProducts[`${productId}:${brandId}`];
